import type { BrandId } from './brands.ts';
import { BUSINESS_TZ_OFFSET_MS } from './internal-ops-business.ts';
import { isCommissionPeriodKey } from './internal-commission-rates.ts';

/**
 * `/internal/ops/commission/accrue` wire contracts + pure accrual math
 * (epic #1034 E5-M3, #1145): the monthly commission accrual that turns
 * verified collected cash (net of the receipt's tax point + actual fees)
 * plus refund clawbacks into per-sales commission statements.
 *
 * Denomination (settled at M3): every money figure is INTEGER micro-USD.
 * It is the only unit BOTH source shapes carry — a verified receipt
 * stamps `credited_usd_micros`, and an online ZPay order credits through
 * a `cash_topup` credit lot's `amount_usd_micros`; CNY cents exist only
 * on CNY receipts, so CNY cannot be the universal statement unit. The
 * commission rate (M2) is dimensionless bps, so commission =
 * round(net_usd_micros × rate_bps / 10000).
 *
 * Statement rows are SIGNED: base rows (receipt / payment_order) are
 * positive; clawback rows are negative. A statement's `total_usd_micros`
 * is the sum of its rows' `commission_usd_micros`.
 */

/** Cron handler name — shared with the control-loops `HANDLERS` registry
 * entry and the `MANUAL_CRON_HANDLERS` catalog. */
export const COMMISSION_ACCRUAL_HANDLER = 'commission_accrual';

/**
 * Sentinel `ops_user_id` for house-attributed money: receipts / orders
 * whose owner stamp is NULL (recycled or unassigned customers). The
 * house line takes no payout but IS totaled for M6 reconciliation. Not a
 * real ops user — never resolved against `ops_users`.
 */
export const COMMISSION_HOUSE_OWNER = 'house';

export const COMMISSION_STATEMENT_STATUSES = ['draft', 'confirmed'] as const;
export type CommissionStatementStatus = (typeof COMMISSION_STATEMENT_STATUSES)[number];

export function isCommissionStatementStatus(value: unknown): value is CommissionStatementStatus {
  return (
    typeof value === 'string' &&
    (COMMISSION_STATEMENT_STATUSES as readonly string[]).includes(value)
  );
}

export const COMMISSION_STATEMENT_ROW_SOURCE_TYPES = [
  'receipt',
  'payment_order',
  'clawback',
  'adjustment',
] as const;
export type CommissionStatementRowSourceType =
  (typeof COMMISSION_STATEMENT_ROW_SOURCE_TYPES)[number];

export function isCommissionStatementRowSourceType(
  value: unknown,
): value is CommissionStatementRowSourceType {
  return (
    typeof value === 'string' &&
    (COMMISSION_STATEMENT_ROW_SOURCE_TYPES as readonly string[]).includes(value)
  );
}

/** One `commission_statements` row. */
export interface CommissionStatementRecord {
  id: string;
  /** Sales owner, or {@link COMMISSION_HOUSE_OWNER} for the house line. */
  ops_user_id: string;
  brand: BrandId;
  /** UTC+8 calendar-month key 'YYYY-MM'. */
  period_start: string;
  /** UTC+8 calendar-month key of the following month (exclusive). */
  period_end: string;
  status: CommissionStatementStatus;
  /** Sum of the statement's row commissions (signed micro-USD). */
  total_usd_micros: number;
  created_at: string;
  updated_at: string;
}

/** One `commission_statement_rows` row — the full per-document derivation. */
export interface CommissionStatementRowRecord {
  id: string;
  statement_id: string;
  source_type: CommissionStatementRowSourceType;
  /** Receipt / payment_order id for base rows; the refund ledger id for clawbacks. */
  source_id: string;
  gross_usd_micros: number;
  /** Basis points from the receipt row (0 for online orders / clawbacks with no tax point). */
  tax_point_bps: number;
  fees_usd_micros: number;
  /** gross − fees − tax; negative on clawback rows. */
  net_usd_micros: number;
  /** Resolved (brand, period) rate applied to this row. */
  rate_bps: number;
  /** round(net × rate_bps / 10000); negative on clawback rows. */
  commission_usd_micros: number;
  created_at: string;
}

// ─── period window ─────────────────────────────────────────────────────────

/**
 * The UTC instant bounds of a UTC+8 calendar-month period. `[startMs,
 * endMs)` is half-open: a money event stamped exactly at `startMs`
 * belongs to this period, one at `endMs` belongs to the next. The ISO
 * twins gate the TEXT timestamp columns (`credited_at`, `paid_at`,
 * `occurred_at`) directly — all three are written as `Date#toISOString()`
 * so lexicographic comparison is chronological.
 */
export interface CommissionPeriodWindow {
  /** The period key 'YYYY-MM' this window covers. */
  readonly period: string;
  /** The next period key 'YYYY-MM' (the statement's `period_end`). */
  readonly nextPeriod: string;
  readonly startMs: number;
  readonly endMs: number;
  readonly startIso: string;
  readonly endIso: string;
}

/**
 * Resolve the `[start, end)` UTC window for a UTC+8 month key 'YYYY-MM'.
 * Returns `null` for a malformed key. Pure and deterministic — the single
 * source for the accrual's timestamp gates and the statement's
 * period_start/period_end.
 */
export function commissionPeriodWindow(period: string): CommissionPeriodWindow | null {
  if (!isCommissionPeriodKey(period)) {
    return null;
  }
  const year = Number(period.slice(0, 4));
  const month = Number(period.slice(5, 7));
  const nextYear = month === 12 ? year + 1 : year;
  const nextMonth = month === 12 ? 1 : month + 1;
  // UTC+8 wall-clock midnight of day 1 → the UTC instant that clock reads.
  const startMs = Date.UTC(year, month - 1, 1, 0, 0, 0, 0) - BUSINESS_TZ_OFFSET_MS;
  const endMs = Date.UTC(nextYear, nextMonth - 1, 1, 0, 0, 0, 0) - BUSINESS_TZ_OFFSET_MS;
  return {
    period,
    nextPeriod: `${nextYear}-${String(nextMonth).padStart(2, '0')}`,
    startMs,
    endMs,
    startIso: new Date(startMs).toISOString(),
    endIso: new Date(endMs).toISOString(),
  };
}

// ─── commission math ───────────────────────────────────────────────────────

/**
 * Commission for a net amount at a bps rate: `round(net × rate_bps /
 * 10000)` — the SINGLE terminal round per statement row (all upstream
 * money is already integer micro-USD). Callers pass a non-negative net;
 * clawback rows negate the result themselves so the round stays
 * symmetric with the base row it reverses.
 */
export function commissionForNet(netUsdMicros: number, rateBps: number): number {
  return Math.round((netUsdMicros * rateBps) / 10_000);
}

// ─── /internal/ops/commission/accrue contracts ─────────────────────────────

/**
 * One batched, cursor-resumable accrual pass. Empty body = accrue the
 * current UTC+8 period from the start of the group scan (defaults).
 *
 * Cursor-resume within a control-loops tick: the caller pages by
 * (ops_user_id, brand) group — `cursor` is the exclusive lower bound
 * `"<ops_user_id><brand>"` — feeding `next_cursor` back until it is
 * null. Regeneration is idempotent (DELETE + rebuild of DRAFT statements
 * with deterministic ids), so nothing is persisted between calls and a
 * fresh scan on the next tick is safe.
 */
export interface AccrueCommissionRequest {
  /** UTC+8 month key 'YYYY-MM'; omitted → the current period. */
  period?: string;
  /** `"<ops_user_id><brand>"` exclusive lower bound; null/absent starts the scan. */
  cursor?: string | null;
  /** Max (owner, brand) groups to process this call; platform clamps to a safe range. */
  limit?: number;
  /** Test-only clock override (epoch ms); production omits it and uses now. */
  now_ms?: number;
}

/**
 * A brand skipped because no commission rate is effective for a period it
 * needs (the target period, or a clawback's original period). The
 * accrual writes NOTHING for such a brand — no silent zero-rate accrual —
 * and leaves its existing drafts untouched. `reason` is always
 * `'no_rate'` today; kept structured for future loud-skip causes.
 */
export interface AccrueCommissionFailedBrand {
  brand: BrandId;
  reason: 'no_rate';
}

export interface AccrueCommissionResponse {
  ok: boolean;
  /** The period this pass accrued (resolved from the request or `now`). */
  period: string;
  /** (owner, brand) groups examined this call. */
  scanned: number;
  /** Draft statements (re)built this call. */
  generated: number;
  /** Groups skipped because a confirmed statement already covers them. */
  skipped_confirmed: number;
  /** Brands skipped loudly for a missing rate (deduplicated). */
  failed_brands: AccrueCommissionFailedBrand[];
  /**
   * Brands with money but no `commission_rates` row at all — commission is
   * not configured for them (settled outside the system, router#2012), so
   * they are skipped quietly and do NOT make `ok` false. Once a brand has
   * any rate row, a period without an effective rate lands in
   * `failed_brands` as before. Optional so a caller tolerates a platform
   * that predates the field (treat absent as empty).
   */
  unconfigured_brands?: BrandId[];
  /** Feed back as `cursor` for the next call; null when the scan is done. */
  next_cursor: string | null;
}
